import React from 'react';
import { useTranslation } from 'react-i18next';

import { TRACKING_OPT_OUT } from '@/utils/analytics/trackingOptOut';
import { CopyButton, Icon } from '../../../../components';
import { GlyphBadge, type ShareGlyphName } from '../ShareTiles';
import { ContextBlock, ContextList, ContextItem, ContextName, ContextUrl, ContextUrlRow } from './Summary.styles';

/** One filter the numbers are narrowed to, as the overview names it. */
export type SummaryFilter = {
  key: string;
  /** The filter's name (already translated): "Source", "Tracking link". */
  label: string;
  /** What it is narrowed to (a source, a platform, a website, a link's name). */
  name: string;
  /** A website it names, opened in a new tab. */
  href?: string;
  /** Opens what it names in place (a tracking link's asset). */
  onOpen?: () => void;
  /** A tracking link's address, shown with a copy button. */
  url?: string;
  /** Its picture (a platform's mark, a source's glyph, the link glyph): the first filter's stands for them all. */
  glyph?: ShareGlyphName;
  /** A picture of its own, in the glyph's place (one country's flag, at the badge's 40px). */
  picture?: React.ReactNode;
};

/** What the overview's numbers are narrowed to, each with its way back to the source, beside one picture (the first
 *  filter's). */
export const SummaryFilters = ({ items }: { items: SummaryFilter[] }): React.ReactElement => {
  const { t } = useTranslation();
  const glyph = items[0]?.glyph;
  const picture = items[0]?.picture;
  return (
    <ContextBlock {...TRACKING_OPT_OUT}>
      {picture ?? (glyph ? <GlyphBadge name={glyph} /> : null)}
      <ContextList>
        {items.map((item) => (
          <ContextItem key={item.key}>
            {item.label}
            {item.href ? (
              <ContextName href={item.href} target="_blank" rel="noopener noreferrer">
                {item.name}
                <Icon name="external-link" size={12} />
              </ContextName>
            ) : item.onOpen ? (
              <ContextName as="button" type="button" onClick={item.onOpen}>
                {item.name}
                <Icon name="external-link" size={12} />
              </ContextName>
            ) : (
              <ContextName as="span">{item.name}</ContextName>
            )}
            {item.url ? (
              <ContextUrlRow>
                <ContextUrl title={item.url}>{item.url.replace(/^https?:\/\//, '')}</ContextUrl>
                <CopyButton value={item.url} label={t('Copy link')} size={14} />
              </ContextUrlRow>
            ) : null}
          </ContextItem>
        ))}
      </ContextList>
    </ContextBlock>
  );
};

export default SummaryFilters;
